import * as React from 'react';
import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { action, computed, observable } from 'mobx';
import { sortValues } from '../utils';
import { translate } from './Translate';
import { FilterValue, FilterSearchKeys } from '../models/components/Filter';
import { FilterStore } from '../stores';
import { Filter } from '../models/configuration/filter/Filter';

// Higher-order component to return a filter with data and methods required to function
const withFilter = WrappedComponent => {
    @inject('translationStore')
    @inject('dataStore')
    @inject('filterStore')
    @observer
    class FilterItem extends Component<any> {
        @observable search: string = '';
        @observable expanded: boolean = this.props.dataStore.config.filtersTop
            ? false
            : this.props.filter.hasOwnProperty('expanded')
            ? this.props.filter.expanded
            : true;
        @observable clearIndividualFilters: boolean = this.props.dataStore
            .config.clearIndividualFilters;

        @computed
        get filterSearchKey(): FilterSearchKeys {
            return this.props.filter.filterSearchKey;
        }

        @computed
        get values(): Array<FilterValue> {
            return this.props.filter.values || [];
        }

        @computed
        get filterConfig(): Filter {
            const { dataStore, filter } = this.props;
            const { config } = dataStore;
            return config.filtersConfig.find(
                item => item.property === filter.property
            );
        }

        @computed
        get filteredValues(): Array<FilterValue> {
            const values = this.values
                .filter(value =>
                    value.label
                        .toLowerCase()
                        .includes(this.search.toLowerCase())
                )
                .filter(
                    value =>
                        !this.props.filterStore.isChecked(
                            this.props.filter.property,
                            value.value,
                            this.props.filter.filterSearchKey
                        )
                );
            return this.sortFilter(values);
        }

        @computed
        get filteredCount(): number {
            return this.filteredValues.length + this.selectedValues.length;
        }

        @computed
        get selectedValues(): Array<FilterValue> {
            const values = this.values.filter(value =>
                this.props.filterStore.isChecked(
                    this.props.filter.property,
                    value.value,
                    this.props.filter.filterSearchKey
                )
            );

            return this.sortFilter(values);
        }

        @computed
        get filterDropdownText(): string {
            const { translationStore } = this.props;
            return this.selectedValues.length
                ? this.selectedValues
                      .map(value => translate(translationStore, value.label))
                      .join(', ')
                : translate(translationStore, 'Select filters');
        }

        @computed
        get enableSearch(): boolean {
            return this.props.filter.enableSearch ? true : false;
        }

        @action
        toggleExpanded = () => {
            this.expanded = !this.expanded;
        };

        @action
        filterSearch = (value: string) => {
            this.search = value;
        };

        @action
        clearSearch = () => {
            this.search = '';
        };

        @action
        sortFilter = (values: Array<any>) => {
            if (values && this.filterConfig) {
                const { sort } = this.filterConfig;
                if (sort) {
                    // deref sort object and set key to label since filters are label/value
                    values = sortValues(values, sort);
                } else {
                    // default sort string ASC
                    values = sortValues(values, {
                        type: 'string',
                        key: 'label',
                        direction: 'ASC',
                    });
                }
            }
            return values;
        };

        @computed
        get isRequired(): boolean {
            const { filter, filterStore } = this.props;
            return (filterStore as FilterStore).isFilterRequired(
                filter.property
            );
        }

        render() {
            return (
                <WrappedComponent
                    expanded={this.expanded}
                    toggleExpanded={this.toggleExpanded}
                    selectedCount={this.selectedValues.length}
                    total={this.values.length}
                    search={this.search}
                    clearSearch={this.clearSearch}
                    selectedValues={this.selectedValues}
                    filteredValues={this.filteredValues}
                    filterSearch={this.filterSearch}
                    enableSearch={this.enableSearch}
                    filterDropdownText={this.filterDropdownText}
                    isRequired={this.isRequired}
                    {...this.props}
                />
            );
        }
    }

    return FilterItem;
};

export default withFilter;



// WEBPACK FOOTER //
// ./src/Components/withFilter.tsx